import React, { FC, useEffect, useRef } from "react";
import { styled } from "styled-components";
import { Entry } from "contentful";
import { SwiperCarousel, SwiperSlide } from "../Swiper";
import AnnouncementBarSlide, { AnnouncementBarSlideEntry } from "./AnnouncementBarSlide";
import { Navigation, Pagination, Mousewheel, Keyboard, Autoplay, EffectFade } from 'swiper/modules';

export interface AnnouncementBarEntry extends Entry {
  id: string;
  brand: string;
  parentProps?: any;
  ghostNavEnabled?: boolean;
  fields: {
    name: string;
    slides: AnnouncementBarSlideEntry[];
    autoplay?: number;
    alignment?: string;
    navigation?: boolean;
    theme?: string;
  };
}

const Container = styled.div<{ $brand: string; $alignment?: string; $theme?: string; }>`
  position: relative;
  overflow: hidden;
  height: 38px;
  width: 100%;

    swiper-container {
      height: 100%;
      width: 90%;
      max-width: 550px;
    }

    ${({ $brand, $theme }) => $brand == 'fender' && `
      background-color: ${$theme == 'dark' ? '#191A1F' : '#FFF'};
    `}

    ${({ $brand, $theme }) => $brand == 'presonus' && `
      background-color: ${$theme == 'dark' ? '#1b8efb' : '#FFF'};
    `}
    ${({ $brand, $theme }) => $brand == 'jackson' && `
      background-color: ${$theme == 'dark' ? '#000' : '#FFF'};
    `};
`;

const NavWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  width: 90%;
  max-width: 600px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
  z-index: 2;
`;

const ArrowWrap = styled.button<{ $direction: string }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;
  transition: opacity 0.3s ease;
  transform: ${({ $direction }) =>
    $direction == 'right' ? 'rotate(180deg)' : 'none'};
`;

const RailArrow: FC<{ direction: string; railId: string; theme?: string; brand?: string }> = ({ direction, railId, theme, brand }) => {
  const swiperDirection = direction == 'left' ? 'prev' : 'next';

  let fillColor = '#000';
  if (theme == 'dark') {
    fillColor = '#fff';
  } else if (brand == 'jackson') {
    fillColor = '#4a4a4a';
  }

  return (
    <ArrowWrap
      $direction={direction}
      className={`swiper-button-${swiperDirection}-${railId}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
          d="M11 6.75C11.4142 6.75 11.75 6.41421 11.75 6C11.75 5.58579 11.4142 5.25 11 5.25L11 6.75ZM0.46967 5.46967C0.176777 5.76256 0.176777 6.23744 0.46967 6.53033L5.24264 11.3033C5.53553 11.5962 6.01041 11.5962 6.3033 11.3033C6.59619 11.0104 6.59619 10.5355 6.3033 10.2426L2.06066 6L6.3033 1.75736C6.59619 1.46447 6.59619 0.989592 6.3033 0.696699C6.01041 0.403805 5.53553 0.403805 5.24264 0.696699L0.46967 5.46967ZM11 5.25L1 5.25L1 6.75L11 6.75L11 5.25Z"
          fill={fillColor}
        />
      </svg>
    </ArrowWrap>
  );
};

const AnnouncementBar: FC<AnnouncementBarEntry> = ({
  id,
  sys,
  parentProps,
  brand,
  ghostNavEnabled,
  fields: {
    slides,
    autoplay,
    alignment,
    navigation,
    theme
  }
}) => {
  const hasMultipleSlides = slides.length > 1;
  const swiperRef = useRef<any>(null);

  useEffect(() => {
    const swiperInstance = swiperRef.current?.swiper;

    return () => {
      if (swiperInstance) {
        swiperInstance.off('slideChange');
      }
    };
  }, []);

  return (
    <Container
      className="utility-bar"
      data-component-id={id}
      $brand={brand}
      $alignment={alignment}
      $theme={theme}
    >
      {navigation && hasMultipleSlides && (
        <NavWrapper>
          <RailArrow direction="left" railId={id} theme={theme} brand={brand} />
          <RailArrow direction="right" railId={id} theme={theme} brand={brand} />
        </NavWrapper>
      )}
      <SwiperCarousel
        ref={swiperRef}
        slidesPerView={1}
        spaceBetween={0}
        modules={[Navigation, Pagination, Mousewheel, Keyboard, Autoplay, EffectFade]}
        mousewheel
        keyboard
        loop= {hasMultipleSlides}
        effect="fade"
        speed={800}
        fadeEffect={{ crossFade: true }}
        autoplay={autoplay && hasMultipleSlides ? { delay: autoplay, disableOnInteraction: false } : false}
        navigation={
          navigation && hasMultipleSlides
            ? {
              nextEl: `.swiper-button-next-${id}`,
              prevEl: `.swiper-button-prev-${id}`
            }
          : false
        }
      >
        {slides?.map((slide, index) => (
          <SwiperSlide key={`${slide.sys.id}-${index}`}>
            <AnnouncementBarSlide slide={slide} brand={brand} theme={theme} />
          </SwiperSlide>
        ))}
      </SwiperCarousel>
    </Container>
  );
};

export default AnnouncementBar;
